import { at, toString } from 'lodash';
import {
  EnhancedPaginationProps,
  PaginationControllerState,
  PaginationProps,
  SortBy,
  SortById,
} from './types';
import { SortOrder } from '@ardoq/api-types';
import {
  getCurrentLocale,
  Locale,
  localeCompareNumericLowercase,
} from '@ardoq/locale';

export const extendPaginationProps = (
  props: PaginationProps
): EnhancedPaginationProps => {
  const { currentPageNumber, perPage, totalResults } = props;
  const totalNumberOfPages = Math.ceil(totalResults / perPage);
  const hasPrevPage = currentPageNumber > 1;
  const hasNextPage = totalNumberOfPages - currentPageNumber > 0;

  let numbersToRender: number[] = [];
  let showLessEllipsis = false;
  let showMoreEllipsis = false;

  // case 1: No. of pages <= 7, as we have total 7 placeholders for numbers in pagination component
  if (totalNumberOfPages <= 7) {
    numbersToRender = range(1, totalNumberOfPages);
  } else {
    showLessEllipsis = currentPageNumber - 1 > 3;
    showMoreEllipsis = totalNumberOfPages - currentPageNumber > 3;
  }

  // case 2: When we need right dots only
  if (!showLessEllipsis && showMoreEllipsis) {
    numbersToRender = range(1, 5); // Only first five elements needs to be shown
  }

  // case 3: When we need left dots only
  else if (showLessEllipsis && !showMoreEllipsis) {
    numbersToRender = range(totalNumberOfPages - 4, totalNumberOfPages);
  }

  // case 4: When we need left and right dots
  else if (showLessEllipsis && showMoreEllipsis) {
    const leftSibling = Math.max(currentPageNumber - 1, 1);
    const rightSibling = Math.min(currentPageNumber + 1, totalNumberOfPages);
    numbersToRender = range(leftSibling, rightSibling);
  }

  const showLastNumber = !numbersToRender.includes(totalNumberOfPages); // to show when it is not present in array
  const showFirstNumber = !numbersToRender.includes(1); // 1 is the starting page number

  return {
    hasPrevPage,
    hasNextPage,
    totalNumberOfPages,
    numbersToRender,
    showLastNumber,
    showFirstNumber,
    showMoreEllipsis,
    showLessEllipsis,
  };
};

// Extended method to include the outer range
const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

type SortByParamFn = (a: any) => number | string;
type SortByParam = SortByParamFn | string | null;
type SortByParams = SortByParam[] | SortByParam;

export const toComparableValue = (input: any) => {
  if ([null, undefined, ''].includes(input)) return undefined;
  return typeof input === 'object' || typeof input === 'boolean' || isNaN(input)
    ? toString(input).toLowerCase()
    : parseFloat(toString(input));
};

export const findValue = (dataSource: any, sortByParam?: SortByParam): any => {
  if (Array.isArray(dataSource)) {
    return dataSource.join();
  } else if (typeof sortByParam === 'function') {
    return sortByParam(dataSource);
  } else if (typeof sortByParam === 'string') {
    return at(dataSource, sortByParam).join('');
  }
  return dataSource;
};

const areStringAndNumber = (valA: unknown, valB: unknown) => {
  const types = [typeof valA, typeof valB];
  return types.includes('string') && types.includes('number');
};

const matchType = (
  valA: any,
  valB: any
): [string | number | undefined, string | number | undefined] => {
  const typeA = typeof valA;
  const typeB = typeof valB;
  if (
    (typeA === 'number' && typeB === 'string') ||
    (typeA === 'string' && typeB === 'number') ||
    (typeA === 'string' && typeB === 'string')
  ) {
    return [`${valA}`.trim(), `${valB}`.trim()];
  }
  return [valA, valB];
};

const defaultComparator = (a: number | string, b: number | string): number =>
  a > b ? 1 : -1;

const isFolder = (row: any) => row && row.rowType === 'rowType/FOLDER';

export const smartCompare = (
  a: any,
  b: any,
  sortByParamsIn?: SortByParams,
  sortOrder: SortOrder = SortOrder.ASC,
  options?: { sortNumbersAfterStrings?: boolean },
  localedComparator?: (a: string, b: string, locale: Locale) => number
): number => {
  if (sortOrder === SortOrder.DEFAULT) return 0;

  const locale = getCurrentLocale();
  const sortByParams = Array.isArray(sortByParamsIn)
    ? sortByParamsIn
    : [sortByParamsIn];

  return sortByParams.reduce((acc: number, sortByParam) => {
    if (acc !== 0) return acc;
    if (isFolder(a) && !isFolder(b)) {
      return -1;
    }
    if (isFolder(b) && !isFolder(a)) {
      return 1;
    }
    const comparableValA = toComparableValue(findValue(a, sortByParam));
    const comparableValB = toComparableValue(findValue(b, sortByParam));
    const [comparableA, comparableB] = matchType(
      comparableValA,
      comparableValB
    );
    const shouldFlipNumberAndString =
      areStringAndNumber(comparableValA, comparableValB) &&
      options?.sortNumbersAfterStrings;

    if (comparableA === undefined || comparableB === undefined) {
      if (comparableA === undefined && comparableB === undefined) return 0;
      if (comparableA === undefined) return 1;
      if (comparableB === undefined) return -1;
    } else if (comparableA !== comparableB) {
      return (
        (localedComparator &&
        typeof comparableA === 'string' &&
        typeof comparableB === 'string'
          ? localedComparator(comparableA, comparableB, locale)
          : defaultComparator(comparableA, comparableB)) *
        (sortOrder === SortOrder.ASC ? 1 : -1) *
        (shouldFlipNumberAndString ? -1 : 1)
      );
    } else if (comparableA === comparableB) {
      return 0;
    }
    return acc;
  }, 0);
};

export const smartSort = <DataSource,>(
  inputData: DataSource[],
  sortByParams?: SortByParams,
  sortOrder: SortOrder = SortOrder.ASC,
  options?: { sortNumbersAfterStrings?: boolean }
) => {
  if (sortOrder === SortOrder.DEFAULT) return inputData;

  return [...inputData].sort((a, b) =>
    smartCompare(
      a,
      b,
      sortByParams,
      sortOrder,
      options,
      localeCompareNumericLowercase
    )
  );
};

export const findSortOrder = <T,>(
  state: PaginationControllerState,
  sortBy: SortBy<T>,
  inSortById?: SortById
): [SortOrder, SortById] => {
  const sortById = typeof sortBy === 'string' ? sortBy : inSortById || null;

  if (state.sortById !== sortById) return [SortOrder.ASC, sortById];
  if (state.sortOrder === SortOrder.DEFAULT) return [SortOrder.ASC, sortById];
  if (state.sortOrder === SortOrder.ASC) return [SortOrder.DESC, sortById];
  return [SortOrder.DEFAULT, null];
};

/** Based on settings in LazyPaginationController, determine page range to load into cache */
export const getCacheStartAndEndPages = (
  currentPageNumber: number,
  totalResults: number,
  perPage: number,
  surroundingPagesToCache: number
) => {
  const maxPageNumber = Math.ceil(totalResults / perPage);
  const startPage = Math.max(currentPageNumber - surroundingPagesToCache, 1);
  const endPage = Math.min(
    currentPageNumber + surroundingPagesToCache,
    maxPageNumber
  );
  return { startPage, endPage };
};
